import React, { useState, useEffect, useCallback } from "react";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Plus,
  Settings,
  Users,
  Calendar as CalendarIcon,
  Clock,
  MapPin,
  Video,
  Filter,
  RefreshCw,
  X,
  Check,
  Edit,
  Trash2,
  MoreVertical,
  Eye,
  EyeOff,
  AlertCircle,
  Phone,
  FileText,
  Play,
  TrendingUp,
} from "lucide-react";
import apiService from "../../services/api";

const TeamCalendar = () => {
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teams, setTeams] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [calendarSettings, setCalendarSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState("week");
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [includePrivate, setIncludePrivate] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showNewAppointment, setShowNewAppointment] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [callData, setCallData] = useState([]);
  const [loadingCallData, setLoadingCallData] = useState(false);
  const [selectedCall, setSelectedCall] = useState(null);

  // Fetch user's teams
  const fetchTeams = useCallback(async () => {
    try {
      const response = await apiService.getUserTeams();
      if (response.success) {
        setTeams(response.teams);
        if (response.teams.length > 0 && !selectedTeam) {
          setSelectedTeam(response.teams[0]);
        }
      }
    } catch (error) {
      console.error("Error fetching teams:", error);
      setError("Failed to load teams");
    }
  }, [selectedTeam]);

  // Fetch team calendar settings
  const fetchTeamSettings = useCallback(async () => {
    if (!selectedTeam) return;

    try {
      const response = await apiService.getTeamCalendarSettings(
        selectedTeam.id
      );
      if (response.success) {
        setCalendarSettings(response.settings);
      }
    } catch (error) {
      console.error("Error fetching team settings:", error);
    }
  }, [selectedTeam]);

  // Fetch team members
  const fetchTeamMembers = useCallback(async () => {
    if (!selectedTeam) return;

    try {
      const response = await apiService.getTeamCalendarMembers(selectedTeam.id);
      if (response.success) {
        setTeamMembers(response.members);
        setSelectedMembers(response.members.map((m) => m.userId));
      }
    } catch (error) {
      console.error("Error fetching team members:", error);
    }
  }, [selectedTeam]);

  // Fetch team appointments
  const fetchAppointments = useCallback(async () => {
    if (!selectedTeam) return;

    try {
      setLoading(true);
      setError(null);

      const { startDate, endDate } = getDateRange();

      const params = {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        includePrivate: includePrivate.toString(),
      };

      if (
        selectedMembers.length > 0 &&
        selectedMembers.length < teamMembers.length
      ) {
        params.memberIds = selectedMembers;
      }

      const response = await apiService.getTeamAppointments(
        selectedTeam.id,
        params
      );

      if (response.success) {
        // Filter appointments by assignedTo field
        let filteredAppointments = response.appointments;

        if (
          selectedMembers.length > 0 &&
          selectedMembers.length < teamMembers.length
        ) {
          filteredAppointments = response.appointments.filter((apt) =>
            selectedMembers.includes(apt.assignedTo)
          );
        }

        setAppointments(filteredAppointments);
      }
    } catch (error) {
      console.error("Error fetching appointments:", error);
      setError("Failed to load appointments");
    } finally {
      setLoading(false);
    }
  }, [
    selectedTeam,
    selectedDate,
    viewMode,
    selectedMembers,
    teamMembers,
    includePrivate,
  ]);

  // Fetch call data for appointment
  const fetchCallData = async (appointmentId) => {
    try {
      setLoadingCallData(true);
      const response = await apiService.getAppointmentCalls(appointmentId);
      if (response.success) {
        setCallData(response.calls || []);
        if (response.calls && response.calls.length > 0) {
          setSelectedCall(response.calls[0]);
        }
      }
    } catch (error) {
      console.error("Error fetching call data:", error);
      setCallData([]);
    } finally {
      setLoadingCallData(false);
    }
  };

  // Get date range based on view mode
  const getDateRange = () => {
    const start = new Date(selectedDate);
    const end = new Date(selectedDate);

    switch (viewMode) {
      case "day":
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        break;
      case "week":
        const dayOfWeek = start.getDay();
        start.setDate(start.getDate() - dayOfWeek);
        start.setHours(0, 0, 0, 0);
        end.setDate(start.getDate() + 6);
        end.setHours(23, 59, 59, 999);
        break;
      case "month":
        start.setDate(1);
        start.setHours(0, 0, 0, 0);
        end.setMonth(end.getMonth() + 1);
        end.setDate(0);
        end.setHours(23, 59, 59, 999);
        break;
    }

    return { startDate: start, endDate: end };
  };

  // Sync team calendar
  const handleSyncCalendar = async () => {
    if (!selectedTeam) return;

    try {
      setSyncing(true);
      const response = await apiService.syncTeamCalendar(selectedTeam.id);
      if (response.success) {
        await fetchAppointments();
        await fetchTeamMembers();
      }
    } catch (error) {
      console.error("Error syncing calendar:", error);
      setError("Failed to sync calendar");
    } finally {
      setSyncing(false);
    }
  };

  // Navigate date
  const navigateDate = (direction) => {
    const newDate = new Date(selectedDate);

    switch (viewMode) {
      case "day":
        newDate.setDate(newDate.getDate() + direction);
        break;
      case "week":
        newDate.setDate(newDate.getDate() + direction * 7);
        break;
      case "month":
        newDate.setMonth(newDate.getMonth() + direction);
        break;
    }

    setSelectedDate(newDate);
  };

  // Toggle member filter
  const toggleMemberFilter = (userId) => {
    setSelectedMembers((prev) => {
      if (prev.includes(userId)) {
        return prev.filter((id) => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  // Select all members
  const selectAllMembers = () => {
    setSelectedMembers(teamMembers.map((m) => m.userId));
  };

  // Deselect all members
  const deselectAllMembers = () => {
    setSelectedMembers([]);
  };

  // Get member color
  const getMemberColor = (userId) => {
    const member = teamMembers.find((m) => m.userId === userId);
    return member?.color || "#3B82F6";
  };

  // Delete appointment
  const handleDeleteAppointment = async (appointmentId) => {
    try {
      const response = await apiService.deleteAppointment(appointmentId);
      if (response.success) {
        await fetchAppointments();
        setSelectedAppointment(null);
      }
    } catch (error) {
      console.error("Error deleting appointment:", error);
      setError("Failed to delete appointment");
    }
  };

  // Format date display
  const formatDateDisplay = () => {
    const { startDate, endDate } = getDateRange();

    switch (viewMode) {
      case "day":
        return selectedDate.toLocaleDateString("en-US", {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
        });
      case "week":
        if (startDate.getMonth() === endDate.getMonth()) {
          return `${startDate.toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
          })} - ${endDate.toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
          })}, ${endDate.getFullYear()}`;
        } else {
          return `${startDate.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          })} - ${endDate.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          })}`;
        }
      case "month":
        return selectedDate.toLocaleDateString("en-US", {
          month: "long",
          year: "numeric",
        });
    }
  };

  // Format time
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Format date
  const formatDate = (date) => {
    return date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  // Time slots for calendar (6 AM to 11 PM, 30-minute intervals)
  const timeSlots = Array.from({ length: 34 }, (_, i) => {
    const totalMinutes = 6 * 60 + i * 30;
    const hour = Math.floor(totalMinutes / 60);
    const minute = totalMinutes % 60;
    return { hour, minute };
  });

  // Check if two appointments overlap
  const appointmentsOverlap = (apt1, apt2) => {
    const start1 = new Date(apt1.startTime).getTime();
    const end1 = new Date(apt1.endTime).getTime();
    const start2 = new Date(apt2.startTime).getTime();
    const end2 = new Date(apt2.endTime).getTime();

    return start1 < end2 && start2 < end1;
  };

  // Calculate appointment layout for overlapping events
  const calculateAppointmentLayout = (appointmentsForPeriod) => {
    if (appointmentsForPeriod.length === 0) return [];

    // Sort by start time, then by duration (longer first)
    const sorted = [...appointmentsForPeriod].sort((a, b) => {
      const startDiff = new Date(a.startTime) - new Date(b.startTime);
      if (startDiff !== 0) return startDiff;

      const durationA = new Date(a.endTime) - new Date(a.startTime);
      const durationB = new Date(b.endTime) - new Date(b.startTime);
      return durationB - durationA;
    });

    // Assign columns to appointments
    const columns = [];
    const appointmentLayouts = [];

    sorted.forEach((apt) => {
      // Find the first column where this appointment doesn't overlap
      let columnIndex = 0;
      let placed = false;

      while (!placed) {
        if (!columns[columnIndex]) {
          columns[columnIndex] = [];
        }

        const overlaps = columns[columnIndex].some((existingApt) =>
          appointmentsOverlap(apt, existingApt)
        );

        if (!overlaps) {
          columns[columnIndex].push(apt);
          placed = true;
        } else {
          columnIndex++;
        }
      }

      appointmentLayouts.push({
        appointment: apt,
        column: columnIndex,
      });
    });

    // Calculate widths and positions
    return appointmentLayouts.map((layout) => {
      const { appointment, column } = layout;

      // Find all appointments that overlap with this one
      const overlappingColumns = new Set();
      sorted.forEach((otherApt) => {
        if (appointmentsOverlap(appointment, otherApt)) {
          const otherLayout = appointmentLayouts.find(
            (l) => l.appointment.id === otherApt.id
          );
          if (otherLayout) {
            overlappingColumns.add(otherLayout.column);
          }
        }
      });

      const totalColumns = overlappingColumns.size;
      const width = totalColumns > 0 ? 100 / totalColumns : 100;
      const left = column * width;

      return {
        ...appointment,
        layoutWidth: width,
        layoutLeft: left,
        layoutColumn: column,
        layoutTotalColumns: totalColumns,
      };
    });
  };

  // Get appointments for a specific time slot with layout
  const getAppointmentsForSlot = (date, hour, minute) => {
    const slotAppointments = appointments.filter((apt) => {
      const aptStart = new Date(apt.startTime);
      return (
        aptStart.getFullYear() === date.getFullYear() &&
        aptStart.getMonth() === date.getMonth() &&
        aptStart.getDate() === date.getDate() &&
        aptStart.getHours() === hour &&
        aptStart.getMinutes() === minute
      );
    });

    return calculateAppointmentLayout(slotAppointments);
  };

  // Get all appointments for a day (for calculating overlaps across slots)
  const getAppointmentsForDayWithLayout = (date) => {
    const dayAppointments = appointments.filter((apt) => {
      const aptStart = new Date(apt.startTime);
      const aptEnd = new Date(apt.endTime);
      const dayStart = new Date(date);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(date);
      dayEnd.setHours(23, 59, 59, 999);

      return (
        (aptStart >= dayStart && aptStart <= dayEnd) ||
        (aptEnd >= dayStart && aptEnd <= dayEnd) ||
        (aptStart <= dayStart && aptEnd >= dayEnd)
      );
    });

    return calculateAppointmentLayout(dayAppointments);
  };

  // Get appointments for a specific day (for month view)
  const getAppointmentsForDay = (date) => {
    return appointments.filter((apt) => {
      const aptStart = new Date(apt.startTime);
      return (
        aptStart.getFullYear() === date.getFullYear() &&
        aptStart.getMonth() === date.getMonth() &&
        aptStart.getDate() === date.getDate()
      );
    });
  };

  // Calculate appointment height based on duration
  const calculateAppointmentHeight = (appointment) => {
    const start = new Date(appointment.startTime);
    const end = new Date(appointment.endTime);
    const duration = (end - start) / (1000 * 60);
    const slots = Math.ceil(duration / 30);
    return slots * 60;
  };

  // Get appointment color
  const getAppointmentColor = (appointment) => {
    if (appointment.isPrivate || appointment.visibility === "private") {
      return "#6B7280";
    }
    return getMemberColor(appointment.assignedTo);
  };

  // Effects
  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);

  useEffect(() => {
    if (selectedTeam) {
      fetchTeamSettings();
      fetchTeamMembers();
    }
  }, [selectedTeam, fetchTeamSettings, fetchTeamMembers]);

  useEffect(() => {
    if (selectedTeam && teamMembers.length > 0) {
      fetchAppointments();
    }
  }, [
    selectedTeam,
    selectedDate,
    viewMode,
    selectedMembers,
    includePrivate,
    teamMembers.length,
  ]);

  useEffect(() => {
    if (selectedAppointment?.id) {
      fetchCallData(selectedAppointment.id);
    } else {
      setCallData([]);
      setSelectedCall(null);
    }
  }, [selectedAppointment?.id]);

  // Appointment Block Component
  const AppointmentBlock = ({
    appointment,
    height,
    onClick,
    layoutWidth,
    layoutLeft,
  }) => {
    const isPrivate =
      appointment.isPrivate || appointment.visibility === "private";
    const color = getAppointmentColor(appointment);
    const member = teamMembers.find((m) => m.userId === appointment.assignedTo);

    return (
      <div
        className={`absolute rounded-lg transition-all border-l-4 overflow-hidden shadow-lg ${
          isPrivate
            ? "opacity-60"
            : "cursor-pointer hover:opacity-90 hover:shadow-xl hover:z-10"
        }`}
        style={{
          backgroundColor: color,
          borderLeftColor: color,
          height: height ? `${Math.max(height - 4, 46)}px` : "auto",
          minHeight: "46px",
          width: layoutWidth ? `${layoutWidth}%` : "calc(100% - 8px)",
          left: layoutLeft !== undefined ? `${layoutLeft}%` : "4px",
          right: layoutLeft !== undefined ? "auto" : "4px",
        }}
        onClick={() => !isPrivate && onClick && onClick()}
      >
        <div className="p-2 h-full flex flex-col">
          <div className="flex items-start gap-2 mb-1">
            <div className="flex-1 min-w-0">
              <div className="text-white font-semibold text-sm truncate flex items-center gap-1">
                {isPrivate && <EyeOff size={12} />}
                {appointment.title}
              </div>
            </div>
          </div>

          <div className="text-white/80 text-xs flex items-center gap-1 mb-1">
            <Clock size={10} />
            {formatTime(appointment.startTime)}
          </div>

          {!isPrivate && member && (
            <div className="flex items-center gap-1 mt-auto">
              <div
                className="w-5 h-5 rounded-full flex items-center justify-center text-white text-xs font-semibold border-2"
                style={{
                  backgroundColor: member.color,
                  borderColor: "rgba(255,255,255,0.3)",
                }}
              >
                {member.user.name.charAt(0).toUpperCase()}
              </div>
              <span className="text-white/70 text-xs truncate">
                {member.user.name}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Day View Component
  const renderDayView = () => {
    // Get all appointments for the day with layout information
    const dayAppointmentsWithLayout =
      getAppointmentsForDayWithLayout(selectedDate);

    return (
      <div className="flex-1 overflow-auto bg-[#0A0B0F]">
        <div className="min-w-full">
          <div className="relative">
            {timeSlots.map((slot, slotIndex) => {
              // Filter appointments that start at this slot
              const slotAppointments = dayAppointmentsWithLayout.filter(
                (apt) => {
                  const aptStart = new Date(apt.startTime);
                  return (
                    aptStart.getHours() === slot.hour &&
                    aptStart.getMinutes() === slot.minute
                  );
                }
              );

              const timeLabel = `${slot.hour
                .toString()
                .padStart(2, "0")}:${slot.minute.toString().padStart(2, "0")}`;

              return (
                <div
                  key={slotIndex}
                  className="flex border-b border-gray-800/50 hover:bg-gray-900/20 transition-colors"
                  style={{ minHeight: "60px" }}
                >
                  <div className="w-20 flex-shrink-0 p-3 text-xs text-gray-500 font-medium text-right border-r border-gray-800">
                    {timeLabel}
                  </div>

                  <div className="flex-1 p-2 relative">
                    {slotAppointments.map((appointment) => (
                      <AppointmentBlock
                        key={appointment.id}
                        appointment={appointment}
                        height={calculateAppointmentHeight(appointment)}
                        onClick={() => setSelectedAppointment(appointment)}
                        layoutWidth={appointment.layoutWidth}
                        layoutLeft={appointment.layoutLeft}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // Week View Component
  const renderWeekView = () => {
    const { startDate } = getDateRange();
    const days = Array.from({ length: 7 }, (_, i) => {
      const day = new Date(startDate);
      day.setDate(day.getDate() + i);
      return day;
    });

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Calculate layout for each day
    const daysWithLayouts = days.map((day) => ({
      date: day,
      appointments: getAppointmentsForDayWithLayout(day),
    }));

    return (
      <div className="flex-1 overflow-auto bg-[#0A0B0F]">
        <div className="min-w-full">
          <div className="flex border-b border-gray-800 sticky top-0 bg-[#13161B] z-10">
            <div className="w-20 flex-shrink-0 border-r border-gray-800"></div>
            {days.map((day, index) => {
              const isToday = day.getTime() === today.getTime();
              return (
                <div
                  key={index}
                  className="flex-1 p-4 text-center border-r border-gray-800 last:border-r-0"
                >
                  <div className="text-xs text-gray-500 mb-1 font-medium uppercase tracking-wider">
                    {day.toLocaleDateString("en-US", { weekday: "short" })}
                  </div>
                  <div
                    className={`text-2xl font-bold ${
                      isToday ? "text-blue-400" : "text-white"
                    }`}
                  >
                    {day.getDate()}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="relative">
            {timeSlots.map((slot, slotIndex) => {
              const timeLabel = `${slot.hour
                .toString()
                .padStart(2, "0")}:${slot.minute.toString().padStart(2, "0")}`;

              return (
                <div
                  key={slotIndex}
                  className="flex border-b border-gray-800/50 hover:bg-gray-900/10 transition-colors"
                  style={{ minHeight: "60px" }}
                >
                  <div className="w-20 flex-shrink-0 p-3 text-xs text-gray-500 font-medium text-right border-r border-gray-800">
                    {timeLabel}
                  </div>

                  {daysWithLayouts.map((dayData, dayIndex) => {
                    // Filter appointments that start at this slot for this day
                    const slotAppointments = dayData.appointments.filter(
                      (apt) => {
                        const aptStart = new Date(apt.startTime);
                        return (
                          aptStart.getHours() === slot.hour &&
                          aptStart.getMinutes() === slot.minute
                        );
                      }
                    );

                    return (
                      <div
                        key={dayIndex}
                        className="flex-1 p-1 border-r border-gray-800/50 last:border-r-0 relative"
                      >
                        {slotAppointments.map((appointment) => (
                          <AppointmentBlock
                            key={appointment.id}
                            appointment={appointment}
                            height={calculateAppointmentHeight(appointment)}
                            onClick={() => setSelectedAppointment(appointment)}
                            layoutWidth={appointment.layoutWidth}
                            layoutLeft={appointment.layoutLeft}
                          />
                        ))}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // Month View Component
  const renderMonthView = () => {
    const { startDate } = getDateRange();
    const monthStart = new Date(startDate);
    monthStart.setDate(1);

    const firstDayOfWeek = monthStart.getDay();
    const calendarStart = new Date(monthStart);
    calendarStart.setDate(calendarStart.getDate() - firstDayOfWeek);

    const days = Array.from({ length: 42 }, (_, i) => {
      const day = new Date(calendarStart);
      day.setDate(day.getDate() + i);
      return day;
    });

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return (
      <div className="flex-1 overflow-auto bg-[#0A0B0F] p-4">
        <div className="grid grid-cols-7 gap-px bg-gray-800 rounded-lg overflow-hidden border border-gray-800">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
            <div
              key={day}
              className="bg-[#13161B] p-3 text-center text-xs font-semibold text-gray-400 uppercase tracking-wider"
            >
              {day}
            </div>
          ))}

          {days.map((day, index) => {
            const isCurrentMonth = day.getMonth() === selectedDate.getMonth();
            const isToday = day.getTime() === today.getTime();
            const dayAppointments = getAppointmentsForDay(day);

            return (
              <div
                key={index}
                className={`bg-[#13161B] min-h-[120px] p-2 ${
                  !isCurrentMonth ? "opacity-40" : ""
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div
                    className={`text-sm font-semibold ${
                      isToday
                        ? "bg-blue-600 text-white w-7 h-7 rounded-full flex items-center justify-center"
                        : "text-white"
                    }`}
                  >
                    {day.getDate()}
                  </div>
                  {dayAppointments.length > 0 && (
                    <div className="text-xs text-gray-500 font-medium">
                      {dayAppointments.length}
                    </div>
                  )}
                </div>

                <div className="space-y-1">
                  {dayAppointments.slice(0, 3).map((appointment) => {
                    const isPrivate =
                      appointment.isPrivate ||
                      appointment.visibility === "private";
                    const color = getAppointmentColor(appointment);
                    const member = teamMembers.find(
                      (m) => m.userId === appointment.assignedTo
                    );

                    return (
                      <div
                        key={appointment.id}
                        className={`text-xs p-1.5 rounded border-l-2 ${
                          isPrivate
                            ? "opacity-60"
                            : "cursor-pointer hover:opacity-80"
                        }`}
                        style={{
                          backgroundColor: `${color}20`,
                          borderLeftColor: color,
                        }}
                        onClick={() =>
                          !isPrivate && setSelectedAppointment(appointment)
                        }
                      >
                        <div className="flex items-center gap-1">
                          {isPrivate && (
                            <EyeOff size={10} className="text-gray-400" />
                          )}
                          <span
                            className="font-medium truncate"
                            style={{ color }}
                          >
                            {formatTime(appointment.startTime)}
                          </span>
                        </div>
                        <div className="text-white truncate font-medium">
                          {appointment.title}
                        </div>
                        {!isPrivate && member && (
                          <div className="text-gray-400 text-xs truncate">
                            {member.user.name}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {dayAppointments.length > 3 && (
                    <div className="text-xs text-gray-500 text-center py-1">
                      +{dayAppointments.length - 3} more
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Member Filter Sidebar Component
  const MemberFilterSidebar = () => (
    <div className="w-72 border-r border-gray-800 bg-[#13161B] p-5 overflow-y-auto">
      <div className="mb-6">
        <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2 uppercase tracking-wider">
          <Users size={16} className="text-blue-400" />
          Team Members
        </h3>

        <div className="flex gap-2 mb-4">
          <button
            onClick={selectAllMembers}
            className="flex-1 px-3 py-2 text-xs bg-[#0C0E12] border border-gray-700 rounded-lg text-gray-300 hover:border-blue-500 hover:text-blue-400 transition-all font-medium"
          >
            Select All
          </button>
          <button
            onClick={deselectAllMembers}
            className="flex-1 px-3 py-2 text-xs bg-[#0C0E12] border border-gray-700 rounded-lg text-gray-300 hover:border-red-500 hover:text-red-400 transition-all font-medium"
          >
            Clear
          </button>
        </div>
      </div>

      <div className="space-y-2 mb-6">
        {teamMembers.map((member) => {
          const isSelected = selectedMembers.includes(member.userId);

          return (
            <button
              key={member.userId}
              onClick={() => toggleMemberFilter(member.userId)}
              className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all ${
                isSelected
                  ? "bg-[#0C0E12] border-2 border-blue-500/50 shadow-lg"
                  : "bg-[#0A0B0F] border border-gray-700 hover:border-gray-600"
              }`}
            >
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md"
                style={{ backgroundColor: member.color }}
              >
                {member.user.name.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 text-left min-w-0">
                <div className="text-sm font-semibold text-white truncate">
                  {member.user.name}
                </div>
                <div className="text-xs text-gray-500 capitalize font-medium">
                  {member.role}
                </div>
              </div>
              <div
                className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                  isSelected ? "bg-blue-600 border-blue-600" : "border-gray-600"
                }`}
              >
                {isSelected && <Check size={14} className="text-white" />}
              </div>
            </button>
          );
        })}
      </div>

      <div className="border-t border-gray-800 pt-5">
        <h4 className="text-xs font-bold text-gray-400 mb-3 uppercase tracking-wider">
          Privacy Settings
        </h4>
        <button
          onClick={() => setIncludePrivate(!includePrivate)}
          className={`w-full flex items-center justify-between gap-3 p-3 rounded-lg transition-all ${
            includePrivate
              ? "bg-blue-900/20 border border-blue-500/50"
              : "bg-[#0A0B0F] border border-gray-700 hover:border-gray-600"
          }`}
        >
          <div className="flex items-center gap-2">
            {includePrivate ? (
              <Eye size={16} className="text-blue-400" />
            ) : (
              <EyeOff size={16} className="text-gray-400" />
            )}
            <div className="text-left">
              <div className="text-sm text-white font-semibold">
                {includePrivate ? "Showing Private" : "Hiding Private"}
              </div>
              <div className="text-xs text-gray-500">
                {includePrivate ? "Your events visible" : "Shown as busy"}
              </div>
            </div>
          </div>
          <div
            className={`w-11 h-6 rounded-full transition-all ${
              includePrivate ? "bg-blue-600" : "bg-gray-600"
            }`}
          >
            <div
              className={`w-5 h-5 bg-white rounded-full m-0.5 transition-transform shadow-md ${
                includePrivate ? "translate-x-5" : "translate-x-0"
              }`}
            />
          </div>
        </button>
      </div>
    </div>
  );

  // New Appointment Modal
  const NewAppointmentModal = () => {
    const [formData, setFormData] = useState({
      title: "",
      description: "",
      startTime: "",
      endTime: "",
      type: "meeting",
      priority: "medium",
      visibility: "team",
      location: "",
      meetingUrl: "",
      assignedTo: "",
    });
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async (e) => {
      e.preventDefault();

      if (!formData.assignedTo) {
        alert("Please select a team member to assign this appointment to");
        return;
      }

      setSubmitting(true);

      try {
        const response = await apiService.createAppointment(formData);

        if (response.success) {
          setShowNewAppointment(false);
          await fetchAppointments();
        }
      } catch (error) {
        console.error("Error creating appointment:", error);
        setError("Failed to create appointment");
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-[#13161B] rounded-xl border border-gray-700 max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
          <div className="p-6 border-b border-gray-700 flex items-center justify-between sticky top-0 bg-[#13161B] z-10">
            <h2 className="text-xl font-bold text-white">
              New Team Appointment
            </h2>
            <button
              onClick={() => setShowNewAppointment(false)}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X size={24} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                Title *
              </label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                className="w-full px-4 py-3 bg-[#0C0E12] border border-gray-700 rounded-lg text-white focus:border-blue-500 focus:outline-none transition-colors"
                placeholder="Enter appointment title"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                Assign To *
              </label>
              <select
                required
                value={formData.assignedTo}
                onChange={(e) =>
                  setFormData({ ...formData, assignedTo: e.target.value })
                }
                className="w-full px-4 py-3 bg-[#0C0E12] border border-gray-700 rounded-lg text-white focus:border-blue-500 focus:outline-none transition-colors"
              >
                <option value="">Select team member</option>
                {teamMembers.map((member) => (
                  <option key={member.userId} value={member.userId}>
                    {member.user.name} ({member.role})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                Visibility
              </label>
              <select
                value={formData.visibility}
                onChange={(e) =>
                  setFormData({ ...formData, visibility: e.target.value })
                }
                className="w-full px-4 py-3 bg-[#0C0E12] border border-gray-700 rounded-lg text-white focus:border-blue-500 focus:outline-none transition-colors"
              >
                <option value="team">Team - Visible to team members</option>
                <option value="public">
                  Public - Visible to entire company
                </option>
                <option value="private">Private - Only you and assignee</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                className="w-full px-4 py-3 bg-[#0C0E12] border border-gray-700 rounded-lg text-white focus:border-blue-500 focus:outline-none transition-colors"
                rows={3}
                placeholder="Add description..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Start Time *
                </label>
                <input
                  type="datetime-local"
                  required
                  value={formData.startTime}
                  onChange={(e) =>
                    setFormData({ ...formData, startTime: e.target.value })
                  }
                  className="w-full px-4 py-3 bg-[#0C0E12] border border-gray-700 rounded-lg text-white focus:border-blue-500 focus:outline-none transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  End Time *
                </label>
                <input
                  type="datetime-local"
                  required
                  value={formData.endTime}
                  onChange={(e) =>
                    setFormData({ ...formData, endTime: e.target.value })
                  }
                  className="w-full px-4 py-3 bg-[#0C0E12] border border-gray-700 rounded-lg text-white focus:border-blue-500 focus:outline-none transition-colors"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Type
                </label>
                <select
                  value={formData.type}
                  onChange={(e) =>
                    setFormData({ ...formData, type: e.target.value })
                  }
                  className="w-full px-4 py-3 bg-[#0C0E12] border border-gray-700 rounded-lg text-white focus:border-blue-500 focus:outline-none transition-colors"
                >
                  <option value="meeting">Meeting</option>
                  <option value="call">Call</option>
                  <option value="site_visit">Site Visit</option>
                  <option value="consultation">Consultation</option>
                  <option value="demo">Demo</option>
                  <option value="follow_up">Follow Up</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Priority
                </label>
                <select
                  value={formData.priority}
                  onChange={(e) =>
                    setFormData({ ...formData, priority: e.target.value })
                  }
                  className="w-full px-4 py-3 bg-[#0C0E12] border border-gray-700 rounded-lg text-white focus:border-blue-500 focus:outline-none transition-colors"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                Location
              </label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) =>
                  setFormData({ ...formData, location: e.target.value })
                }
                className="w-full px-4 py-3 bg-[#0C0E12] border border-gray-700 rounded-lg text-white focus:border-blue-500 focus:outline-none transition-colors"
                placeholder="Meeting location"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                Meeting URL
              </label>
              <input
                type="url"
                value={formData.meetingUrl}
                onChange={(e) =>
                  setFormData({ ...formData, meetingUrl: e.target.value })
                }
                className="w-full px-4 py-3 bg-[#0C0E12] border border-gray-700 rounded-lg text-white focus:border-blue-500 focus:outline-none transition-colors"
                placeholder="https://zoom.us/j/..."
              />
            </div>

            <div className="flex gap-3 pt-4 border-t border-gray-700">
              <button
                type="button"
                onClick={() => setShowNewAppointment(false)}
                className="flex-1 px-4 py-3 bg-[#0C0E12] border border-gray-700 rounded-lg text-gray-300 hover:bg-gray-800 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 px-4 py-3 text-white rounded-lg font-semibold transition-all duration-300 disabled:opacity-50 hover:shadow-xl"
                style={{
                  background:
                    "radial-gradient(50% 50% at 50% 50%, #427BCA 0%, #2F578F 100%)",
                }}
              >
                {submitting ? "Creating..." : "Create Appointment"}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Appointment Details Modal
  const AppointmentDetailsModal = () => {
    if (!selectedAppointment) return null;

    const color = getAppointmentColor(selectedAppointment);
    const member = teamMembers.find(
      (m) => m.userId === selectedAppointment.assignedTo
    );
    const call = selectedCall?.callData;

    return (
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-[#13161B] rounded-xl border border-gray-700 max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
          <div className="p-6 border-b border-gray-700 flex items-center justify-between sticky top-0 bg-[#13161B] z-10">
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-white mb-1">
                {selectedAppointment.title}
              </h2>
              {member && (
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <div
                    className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-semibold"
                    style={{ backgroundColor: member.color }}
                  >
                    {member.user.name.charAt(0).toUpperCase()}
                  </div>
                  <span>Assigned to {member.user.name}</span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() =>
                  console.log("Edit appointment:", selectedAppointment.id)
                }
                className="p-2 text-gray-400 hover:text-white transition-colors"
              >
                <Edit size={20} />
              </button>
              <button
                onClick={() => handleDeleteAppointment(selectedAppointment.id)}
                className="p-2 text-gray-400 hover:text-red-500 transition-colors"
              >
                <Trash2 size={20} />
              </button>
              <button
                onClick={() => setSelectedAppointment(null)}
                className="p-2 text-gray-400 hover:text-white transition-colors"
              >
                <X size={24} />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <span
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white shadow-md"
                  style={{ backgroundColor: color }}
                >
                  {selectedAppointment.status}
                </span>
                <span className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-700 text-gray-200">
                  {selectedAppointment.type}
                </span>
                <span className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-700 text-gray-200">
                  {selectedAppointment.priority}
                </span>
                <span className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-700 text-gray-200">
                  {selectedAppointment.visibility}
                </span>
              </div>

              <div className="flex items-start gap-3">
                <Clock size={20} className="text-blue-400 mt-0.5" />
                <div>
                  <div className="text-white font-semibold text-lg">
                    {formatTime(selectedAppointment.startTime)} -{" "}
                    {formatTime(selectedAppointment.endTime)}
                  </div>
                  <div className="text-sm text-gray-400">
                    {formatDate(new Date(selectedAppointment.startTime))}
                  </div>
                </div>
              </div>

              {selectedAppointment.location && (
                <div className="flex items-start gap-3">
                  <MapPin size={20} className="text-green-400 mt-0.5" />
                  <div className="text-white font-medium">
                    {selectedAppointment.location}
                  </div>
                </div>
              )}

              {selectedAppointment.meetingUrl && (
                <div className="flex items-start gap-3">
                  <Video size={20} className="text-purple-400 mt-0.5" />
                  <a
                    href={selectedAppointment.meetingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:text-blue-300 transition-colors break-all font-medium"
                  >
                    {selectedAppointment.meetingUrl}
                  </a>
                </div>
              )}

              {selectedAppointment.description && (
                <div className="bg-[#0C0E12] border border-gray-700 rounded-lg p-4">
                  <div className="text-sm font-semibold text-gray-400 mb-2">
                    Description
                  </div>
                  <div className="text-white whitespace-pre-wrap leading-relaxed">
                    {selectedAppointment.description}
                  </div>
                </div>
              )}

              {selectedAppointment.attendees &&
                selectedAppointment.attendees.length > 0 && (
                  <div className="bg-[#0C0E12] border border-gray-700 rounded-lg p-4">
                    <div className="text-sm font-semibold text-gray-400 mb-3 flex items-center gap-2">
                      <Users size={16} />
                      Attendees ({selectedAppointment.attendees.length})
                    </div>
                    <div className="space-y-2">
                      {selectedAppointment.attendees.map((attendee, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-3 p-3 bg-[#13161B] rounded-lg"
                        >
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold shadow-md">
                            {(attendee.name || attendee.email)
                              .charAt(0)
                              .toUpperCase()}
                          </div>
                          <div className="flex-1">
                            <div className="text-white font-semibold">
                              {attendee.name || attendee.email}
                            </div>
                            {attendee.name && (
                              <div className="text-xs text-gray-400">
                                {attendee.email}
                              </div>
                            )}
                          </div>
                          {attendee.responseStatus && (
                            <div
                              className={`text-xs px-3 py-1 rounded-full font-semibold ${
                                attendee.responseStatus === "accepted"
                                  ? "bg-green-500/20 text-green-400"
                                  : attendee.responseStatus === "declined"
                                  ? "bg-red-500/20 text-red-400"
                                  : attendee.responseStatus === "tentative"
                                  ? "bg-yellow-500/20 text-yellow-400"
                                  : "bg-gray-500/20 text-gray-400"
                              }`}
                            >
                              {attendee.responseStatus}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>

            {loadingCallData ? (
              <div className="bg-[#0C0E12] border border-gray-700 rounded-xl p-6">
                <div className="text-center text-gray-400">
                  Loading call data...
                </div>
              </div>
            ) : callData.length > 0 ? (
              <div className="space-y-4 border-t border-gray-700 pt-6">
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                  <Phone size={20} className="text-green-400" />
                  Call Recordings
                </h3>

                {callData.length > 1 && (
                  <div className="bg-[#0C0E12] border border-gray-700 rounded-xl p-4">
                    <h4 className="text-sm font-semibold text-gray-400 mb-3">
                      Related Calls ({callData.length})
                    </h4>
                    <div className="space-y-2">
                      {callData.map((c) => (
                        <button
                          key={c.id}
                          onClick={() => setSelectedCall(c)}
                          className={`w-full text-left p-3 rounded-lg transition-all ${
                            selectedCall?.id === c.id
                              ? "bg-blue-500/10 border-2 border-blue-500/50 shadow-lg"
                              : "bg-[#13161B] border border-gray-700 hover:border-gray-600"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Phone size={16} className="text-green-400" />
                              <span className="text-sm text-white font-semibold">
                                {c.phoneNumber}
                              </span>
                            </div>
                            <span className="text-xs text-gray-400">
                              {new Date(c.callStartTime).toLocaleString()}
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {call && (
                  <div className="bg-[#0C0E12] border border-gray-700 rounded-xl p-6 space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-lg font-bold text-white">
                        Call Details
                      </h4>
                      {call.recordingUrl && (
                        <a
                          href={call.recordingUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold shadow-lg"
                        >
                          <Play size={16} />
                          Play Recording
                        </a>
                      )}
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="bg-[#13161B] border border-gray-700 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Clock size={14} className="text-blue-400" />
                          <span className="text-xs text-gray-400 font-semibold">
                            Duration
                          </span>
                        </div>
                        <div className="text-lg font-bold text-white">
                          {Math.floor(call.duration / 60)}m {call.duration % 60}
                          s
                        </div>
                      </div>

                      <div className="bg-[#13161B] border border-gray-700 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Phone size={14} className="text-green-400" />
                          <span className="text-xs text-gray-400 font-semibold">
                            Direction
                          </span>
                        </div>
                        <div className="text-lg font-bold text-white capitalize">
                          {call.direction}
                        </div>
                      </div>

                      {call.qualification && (
                        <>
                          <div className="bg-[#13161B] border border-gray-700 rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <TrendingUp
                                size={14}
                                className="text-purple-400"
                              />
                              <span className="text-xs text-gray-400 font-semibold">
                                Status
                              </span>
                            </div>
                            <div
                              className={`text-lg font-bold ${
                                call.qualification.isQualified
                                  ? "text-green-400"
                                  : "text-red-400"
                              }`}
                            >
                              {call.qualification.isQualified
                                ? "Qualified"
                                : "Not Qualified"}
                            </div>
                          </div>

                          <div className="bg-[#13161B] border border-gray-700 rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <FileText size={14} className="text-yellow-400" />
                              <span className="text-xs text-gray-400 font-semibold">
                                Score
                              </span>
                            </div>
                            <div className="text-lg font-bold text-white">
                              {call.qualification.qualificationScore}/100
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    {call.insights?.summary && (
                      <div className="bg-[#13161B] border border-gray-700 rounded-lg p-4">
                        <h5 className="text-sm font-semibold text-gray-400 mb-2">
                          Call Summary
                        </h5>
                        <p className="text-sm text-gray-300 leading-relaxed">
                          {call.insights.summary}
                        </p>
                      </div>
                    )}

                    {call.transcripts && call.transcripts.length > 0 && (
                      <div className="bg-[#13161B] border border-gray-700 rounded-lg p-4">
                        <h5 className="text-sm font-semibold text-gray-400 mb-3">
                          Transcript
                        </h5>
                        <div className="max-h-64 overflow-y-auto space-y-3">
                          {call.transcripts
                            .slice(0, 5)
                            .map((transcript, index) => (
                              <div
                                key={index}
                                className="flex gap-3 pb-3 border-b border-gray-800 last:border-b-0"
                              >
                                <div className="text-xs text-gray-500 min-w-[60px] font-mono">
                                  {Math.floor(transcript.startTime / 60)}:
                                  {String(
                                    Math.floor(transcript.startTime % 60)
                                  ).padStart(2, "0")}
                                </div>
                                <div className="flex-1">
                                  <div className="text-xs font-semibold text-blue-400 mb-1">
                                    {transcript.speakerLabel}:
                                  </div>
                                  <p className="text-sm text-gray-300 leading-relaxed">
                                    {transcript.text}
                                  </p>
                                </div>
                              </div>
                            ))}
                        </div>
                        {call.transcripts.length > 5 && (
                          <div className="text-center mt-4">
                            <button className="text-sm text-blue-400 hover:text-blue-300 font-semibold">
                              View full transcript →
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : null}

            {selectedAppointment.creator && (
              <div className="pt-4 border-t border-gray-700">
                <div className="text-xs text-gray-500">
                  Created by {selectedAppointment.creator.name} on{" "}
                  {new Date(selectedAppointment.createdAt).toLocaleString()}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#0A0B0F] text-white">
      <div className="flex min-h-screen">
        {teamMembers.length > 0 && <MemberFilterSidebar />}

        <div className="flex-1 flex flex-col">
          <div className="p-6 border-b border-gray-800 bg-[#13161B]">
            {error && (
              <div className="mb-4 bg-red-500/10 border border-red-500/50 rounded-lg p-4 flex items-start gap-3">
                <AlertCircle
                  size={20}
                  className="text-red-500 mt-0.5 flex-shrink-0"
                />
                <div className="flex-1">
                  <div className="text-red-500 font-semibold">Error</div>
                  <div className="text-red-400 text-sm">{error}</div>
                </div>
                <button
                  onClick={() => setError(null)}
                  className="text-red-400 hover:text-red-300 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            )}

            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-3xl font-bold text-white mb-2">
                  Team Calendar
                </h1>
                <p className="text-gray-400 font-medium">
                  {selectedTeam
                    ? `${selectedTeam.name} - Viewing team members' personal appointments`
                    : "Select a team"}
                </p>
              </div>

              <div className="flex items-center gap-3">
                <div className="relative">
                  <select
                    value={selectedTeam?.id || ""}
                    onChange={(e) => {
                      const team = teams.find((t) => t.id === e.target.value);
                      setSelectedTeam(team);
                    }}
                    className="px-4 py-2.5 bg-[#0C0E12] border border-gray-700 rounded-lg text-white focus:border-blue-500 focus:outline-none appearance-none pr-10 font-medium"
                  >
                    {teams.map((team) => (
                      <option key={team.id} value={team.id}>
                        {team.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={16}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                  />
                </div>

                <button
                  onClick={handleSyncCalendar}
                  disabled={syncing}
                  className="p-2.5 bg-[#0C0E12] border border-gray-700 rounded-lg text-gray-300 hover:text-white hover:border-gray-600 transition-colors disabled:opacity-50"
                  title="Sync all team members' calendars"
                >
                  <RefreshCw
                    size={20}
                    className={syncing ? "animate-spin" : ""}
                  />
                </button>

                <button
                  onClick={() => setShowNewAppointment(true)}
                  className="flex items-center gap-2 px-5 py-2.5 text-white rounded-lg font-semibold transition-all duration-300 hover:shadow-xl hover:scale-105"
                  style={{
                    background:
                      "radial-gradient(50% 50% at 50% 50%, #427BCA 0%, #2F578F 100%)",
                  }}
                >
                  <Plus size={20} />
                  New Appointment
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => navigateDate(-1)}
                  className="p-2.5 bg-[#0C0E12] border border-gray-700 rounded-lg text-gray-300 hover:text-white hover:border-gray-600 transition-all hover:shadow-lg"
                >
                  <ChevronLeft size={20} />
                </button>

                <div className="text-xl font-bold text-white min-w-[320px] text-center">
                  {formatDateDisplay()}
                </div>

                <button
                  onClick={() => navigateDate(1)}
                  className="p-2.5 bg-[#0C0E12] border border-gray-700 rounded-lg text-gray-300 hover:text-white hover:border-gray-600 transition-all hover:shadow-lg"
                >
                  <ChevronRight size={20} />
                </button>

                <button
                  onClick={() => setSelectedDate(new Date())}
                  className="px-4 py-2.5 bg-[#0C0E12] border border-gray-700 rounded-lg text-gray-300 hover:text-white hover:border-gray-600 transition-all text-sm font-semibold"
                >
                  Today
                </button>
              </div>

              <div className="flex items-center gap-2 bg-[#0C0E12] border border-gray-700 rounded-lg p-1">
                {["day", "week", "month"].map((mode) => (
                  <button
                    key={mode}
                    onClick={() => setViewMode(mode)}
                    className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${
                      viewMode === mode
                        ? "bg-blue-600 text-white shadow-lg"
                        : "text-gray-400 hover:text-white hover:bg-gray-800"
                    }`}
                  >
                    {mode.charAt(0).toUpperCase() + mode.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {includePrivate && (
            <div className="px-6 py-3 bg-blue-900/20 border-b border-blue-800/30 flex items-center gap-2 text-sm text-blue-300 font-medium">
              <AlertCircle size={16} />
              <span>
                Showing your private appointments. Others see them as "Busy"
                blocks.
              </span>
            </div>
          )}

          {loading ? (
            <div className="flex-1 flex items-center justify-center bg-[#0A0B0F]">
              <div className="text-gray-400 text-lg">
                Loading appointments...
              </div>
            </div>
          ) : error ? (
            <div className="flex-1 flex items-center justify-center bg-[#0A0B0F]">
              <div className="text-red-400 text-lg">{error}</div>
            </div>
          ) : (
            <div className="flex-1 overflow-hidden">
              {viewMode === "day" && renderDayView()}
              {viewMode === "week" && renderWeekView()}
              {viewMode === "month" && renderMonthView()}
            </div>
          )}
        </div>
      </div>

      {showNewAppointment && <NewAppointmentModal />}
      {selectedAppointment && <AppointmentDetailsModal />}
    </div>
  );
};

export default TeamCalendar;
